import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, Card } from "randmarcomps";
import { Info } from "lucide-react";
import type { PurchaseOrder as ApiPurchaseOrder } from "randmar-api-client";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import CancelPurchaseOrder from "../document/actions/CancelPurchaseOrder";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

interface IProps {
  purchaseOrders?: ApiPurchaseOrder[];
  defaultRowsPerPage?: number;
}

interface PurchaseOrderSummaryRow {
  purchaseOrderNumber: string;
  location?: string | null;
  purchaseOrderDate?: number;
  expectedArrivalDate?: number;
  vendorName?: string | null;
  lines: number;
  quantityOutstanding: number;
  estimatedValue: number;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const PurchaseOrdersGrid: React.FC<IProps> = ({ purchaseOrders, defaultRowsPerPage }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);

  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const summarizedRows = useMemo<PurchaseOrderSummaryRow[]>(() => {
    const rows = new Map<string, PurchaseOrderSummaryRow>();

    for (const line of purchaseOrders ?? []) {
      const purchaseOrderNumber = line.PurchaseOrderNumber ?? "";
      if (!purchaseOrderNumber) {
        continue;
      }

      const quantityOutstanding = line.QuantityOutstanding ?? line.QuantityOrdered ?? 0;
      const unitPrice = line.UnitPrice ?? 0;
      const extendedPrice = quantityOutstanding * unitPrice;

      const existing = rows.get(purchaseOrderNumber);
      if (!existing) {
        rows.set(purchaseOrderNumber, {
          purchaseOrderNumber,
          location: line.Location,
          purchaseOrderDate: line.PurchaseOrderDate,
          expectedArrivalDate: line.ExpectedArrivalDate,
          vendorName: line.VendorName,
          lines: 1,
          quantityOutstanding,
          estimatedValue: extendedPrice,
        });
        continue;
      }

      existing.lines += 1;
      existing.quantityOutstanding += quantityOutstanding;
      existing.estimatedValue += extendedPrice;
      existing.location = existing.location || line.Location;
      existing.vendorName = existing.vendorName || line.VendorName;
      existing.purchaseOrderDate = Math.max(existing.purchaseOrderDate ?? 0, line.PurchaseOrderDate ?? 0) || undefined;
      const existingExpected = existing.expectedArrivalDate ?? Number.POSITIVE_INFINITY;
      const incomingExpected = line.ExpectedArrivalDate ?? Number.POSITIVE_INFINITY;
      existing.expectedArrivalDate = Math.min(existingExpected, incomingExpected);
      if (existing.expectedArrivalDate === Number.POSITIVE_INFINITY) {
        existing.expectedArrivalDate = undefined;
      }
    }

    return Array.from(rows.values()).sort((a, b) => b.purchaseOrderNumber.localeCompare(a.purchaseOrderNumber));
  }, [purchaseOrders]);

  if (!summarizedRows.length) {
    return (
      <Alert className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Purchase orders</AlertTitle>
        <AlertDescription>
          Purchase Orders are how Randmar plans inbound inventory. There are no purchase orders to display right now.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Purchase orders</h3>
          <p className="text-[15px] text-slate-600">
            Aggregated totals across purchase order lines.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {summarizedRows.length} purchase order{summarizedRows.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO number
              </TableHead>
              <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Location
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO date
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Expected
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Lines
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Qty outstanding
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Est. value
              </TableHead>
              <TableHead className="w-16 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summarizedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow
                  key={row.purchaseOrderNumber}
                  className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                >
                  <TableCell className="font-semibold">
                    <Link
                      to={Path.document(applicationId, row.purchaseOrderNumber)}
                      className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                    >
                      {row.purchaseOrderNumber}
                    </Link>
                    {row.vendorName && (
                      <p className="mt-1 text-xs text-slate-500">{row.vendorName}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700">{getWarehouseNameFromCode(row.location || undefined)}</TableCell>
                  <TableCell className="text-slate-700">{FormatDateFromInt(row.purchaseOrderDate)}</TableCell>
                  <TableCell className="text-slate-700">{FormatDateFromInt(row.expectedArrivalDate)}</TableCell>
                  <TableCell className="text-right text-slate-700">{row.lines}</TableCell>
                  <TableCell className="text-right text-slate-700">
                    {row.quantityOutstanding.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.estimatedValue} />
                  </TableCell>
                  <TableCell className="text-right">
                    <CancelPurchaseOrder purchaseOrderNumber={row.purchaseOrderNumber} fab withReload />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {summarizedRows.length > rowsPerPage && (
        <TablePaginationBar
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={summarizedRows.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(nextRows) => setRowsPerPage(nextRows)}
        />
      )}
    </Card>
  );
};

export default PurchaseOrdersGrid;
